import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../../assets/styles/LeftNav.css";

import dashboardblack from "../../assets/img/dashboardblack.svg";
import dashboardwhite from "../../assets/img/dashboardwhite.svg";
import crmblack from "../../assets/img/crmblack.svg";
import crmwhite from "../../assets/img/crmwhite.svg";
import contactblack from "../../assets/img/contactblack.svg";
import contactwhite from "../../assets/img/contactwhite.svg";
import taskblack from "../../assets/img/taskblack.svg";
import taskwhite from "../../assets/img/taskwhite.svg";
import blackpush from "../../assets/img/Pushblack.svg"
import whitepushnotificatioin from "../../assets/img/whitepushnotification.svg";
import projectLogo from "../../assets/img/rebax-black.png";
import siteLogo from "../../assets/img/logo-icon.png";
// Import auth service to get user role
import authService from "../../../services/authService";
import adminStaffService from "../../../services/adminStaffService";

// Shared across LeftNav remounts so page navigation doesn't spam goOnline
let lastStaffOnlineAt = 0;

export default function LeftNav() {
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  // Check if current path is Webadmincontact
  const isContactPage = location.pathname === "/Webadmincontact";

  // Get user role on component mount - based on API response structure
  useEffect(() => {
    const initUserData = () => {
      try {
        // Get user data from localStorage (stored after login in authService.js line 41)
        const userData = localStorage.getItem('user');
        console.log('LeftNav: Raw userData from localStorage:', userData);

        if (userData) {
          const user = JSON.parse(userData);
          console.log('LeftNav: Parsed user object:', user);
          console.log('LeftNav: User permissions:', user.permissions);
          console.log('LeftNav: Admin user type:', user.permissions?.admin_user_type);
          console.log('LeftNav: User role:', user.role);

          // Based on your API response structure:
          // Use admin_user_type from permissions if available, otherwise use role
          const effectiveRole = user.permissions?.admin_user_type || user.role || 'admin';
          console.log('LeftNav: Effective role determined:', effectiveRole);
          setUserRole(effectiveRole);
        } else {
          // Fallback: try userRole from localStorage (set in authService.js line 48)
          const storedRole = localStorage.getItem('userRole');
          console.log('LeftNav: Retrieved role from localStorage fallback:', storedRole);
          setUserRole(storedRole || 'admin');
        }
      } catch (err) {
        console.error('Failed to get user role in LeftNav', err);
        setUserRole('admin'); // fallback to 'admin'
      } finally {
        setLoading(false);
      }
    };
    initUserData();
  }, []);

  // Auto-set customer_support staff online + heartbeat - same as AdminBottom.js (mobile).
  // Backend only auto-assigns chats to staff with is_online = true, and staff only see
  // chats assigned to them, so without this they never receive any messages.
  // Heartbeat refreshes updated_at so the backend's 30-min auto-offline doesn't kick in.
  useEffect(() => {
    if (userRole !== 'customer_support') return;

    const markOnline = () => {
      // LeftNav remounts on every page change — throttle repeated calls
      if (Date.now() - lastStaffOnlineAt < 60 * 1000) return;
      lastStaffOnlineAt = Date.now();
      adminStaffService.goOnline().catch(e =>
        console.warn('LeftNav: Failed to auto-set staff online:', e)
      );
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') markOnline();
    };

    markOnline();
    const heartbeat = setInterval(markOnline, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [userRole]);

  // Fetch unread chat counts - exact same flow as AdminBottom.js
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        // This should match your userService.getUnreadSupportCount() API
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/chats/unread-count`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const res = await response.json();
          if (res && res.success) {
            setUnreadChatCount(res.data.count || 0);
          }
        }
      } catch (err) {
        // Silently fail - endpoint may not be deployed yet (same as AdminBottom.js)
        console.warn('Failed to fetch unread chat count:', err);
      }
    };

    if (!loading && userRole) {
      fetchUnreadCounts();
      // Set interval to refresh count periodically (same as AdminBottom.js)
      const interval = setInterval(fetchUnreadCounts, 10000);
      return () => clearInterval(interval);
    }
  }, [loading, userRole]);

  // Define role permissions matching React Native AdminBottom.js
  const isAdmin = userRole === 'admin';
  const isCustomerSupport = userRole === 'customer_support';
  const isMarketing = userRole === 'marketing';
  const isDashboardView = userRole === 'dashboard_view';
  const isAccounts = userRole === 'accounts';

  // Define allowed navigation items per role (exact same logic as AdminBottom.js)
  const showDashboard = isAdmin || isDashboardView;
  const showTasks = isAdmin || isCustomerSupport;
  const showCRM = isAdmin || isCustomerSupport;
  const showPushNotifications = isAdmin || isMarketing;
  const showChat = isAdmin || isCustomerSupport;
  const showBilling = isAccounts;

  // Define all possible navigation items
  const allNavItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/Webadmindashboard",
      icon: dashboardblack,
      activeIcon: dashboardwhite,
      show: showDashboard
    },
    {
      id: "tasks",
      label: "Task Manager",
      path: "/TaskScreenwebadmin",
      icon: taskblack,
      activeIcon: taskwhite,
      show: showTasks
    },
    {
      id: "crm",
      label: "CRM",
      path: "/Webadminaddcrm",
      icon: crmblack,
      activeIcon: crmwhite,
      show: showCRM
    },
    {
      id: "push",
      label: "Push Notification",
      path: "/AdminPushNotification",
      icon: blackpush,
      activeIcon: whitepushnotificatioin,
      show: showPushNotifications
    },
    {
      id: "contacts",
      label: "Contacts",
      path: "/Webadmincontact",
      icon: contactblack,
      activeIcon: contactwhite,
      show: showChat
    },
    // Billing navigation item (for accounts role)
    {
      id: "billing",
      label: "Billing",
      path: "/adminbillingsummary",
      icon: contactblack,
      activeIcon: contactwhite,
      show: showBilling
    },
  ];

  // Filter navigation items based on user role
  let navItems = allNavItems.filter(item => item.show);

  // Fallback pattern: If somehow none of the conditions are true, render at least Dashboard tab (exact same logic as AdminBottom.js lines 244-246)
  const showAnyTab = showDashboard || showTasks || showCRM || showPushNotifications || showChat || showBilling;
  if (!showAnyTab || navItems.length === 0) {
    // Force show Dashboard as fallback
    navItems = [allNavItems.find(item => item.id === 'dashboard')].filter(Boolean);
  }

  if (loading) {
    return (
      <aside className="leftnav1 d-flex flex-column p-3 bg-white shadow-sm" style={{ position: "sticky", top: 0, height: "100vh" }}>
        <div className="mb-4 px-2 rebax-text">REBAX</div>
        <div className="text-center mt-4">Loading...</div>
      </aside>
    );
  }

  return (
    <aside
      className={`leftnav1 d-flex flex-column p-3 bg-white shadow-sm ${isContactPage ? "icon-only" : ""}`}
      style={{ position: "sticky", top: 0, height: "100vh" }}
    >
    <div className="mb-4 px-2 d-flex align-items-center justify-content-center gap-2">
        <img
          src={siteLogo}
          alt="Site Logo"
          style={{ height: '40px', width: 'auto' }}
        />
        {!isContactPage && (
          <img
            src={projectLogo}
            alt="Project Logo"
            style={{ height: '25px', width: 'auto' }}
          />
        )}
      </div>
      <nav className="nav flex-column gap-2 mt-4">
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) =>
              `nav-link mt-2 w-100 text-start d-flex align-items-center ${isActive ? "active" : ""}`
            }
          >
            {({ isActive }) => (
              <>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={isActive ? item.activeIcon : item.icon}
                    alt={item.label}
                    className="me-2"
                    width="24"
                    height="24"
                  />
                  {/* Unread chat count badge - matching AdminBottom.js lines 179-195 */}
                  {!isActive && item.id === 'contacts' && unreadChatCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-2px',
                        backgroundColor: 'red',
                        borderRadius: '10px',
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: 'white'
                      }}
                    >
                      {unreadChatCount}
                    </span>
                  )}
                </div>
                {!isContactPage && item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
